<script setup>
import { reactive, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import VariableTextArea from './VariableTextArea.vue';

const emit = defineEmits(['submit', 'cancel']);
const { t } = useI18n();

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  inboxes: useMapGetter('inboxes/getInboxes'), // Usaremos todos los inboxes y filtraremos
};

const initialState = {
  title: '',
  message: '',
  inboxId: null,
  scheduledAt: null,
  selectedAudience: [],
  labelType: 'conversation', // 'conversation' o 'contact'
  imageFile: null,
  imagePreview: null,
};

const state = reactive({ ...initialState });

const rules = {
  title: { required, minLength: minLength(1) },
  message: { required, minLength: minLength(1) },
  inboxId: { required },
  scheduledAt: { required },
  selectedAudience: { required },
  labelType: { required },
};

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => formState.uiFlags.value.isCreating);

const currentDateTime = computed(() => {
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

// Filtrar solo inboxes de Evolution API (Channel::Api con webhook)
const evolutionAPIInboxes = computed(() => {
  return formState.inboxes.value.filter(inbox => {
    return (
      inbox.channel_type === 'Channel::Api' &&
      inbox.webhook_url &&
      inbox.webhook_url.includes('/chatwoot/webhook/')
    );
  });
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const audienceList = computed(() => {
  // Usar las mismas etiquetas para ambos tipos (conversation y contact)
  const sourceData = formState.labels.value || [];

  // Usar la función de mapeo estándar para ambos casos
  const options = mapToOptions(sourceData, 'id', 'title');

  return options;
});

const inboxOptions = computed(() =>
  mapToOptions(evolutionAPIInboxes.value, 'id', 'name')
);

const getErrorMessage = field => {
  if (v$.value[field].$error) {
    switch (field) {
      case 'title':
        return t('CAMPAIGN.QRBUHO.CREATE.FORM.TITLE.ERROR');
      case 'message':
        return t('CAMPAIGN.QRBUHO.CREATE.FORM.MESSAGE.ERROR');
      case 'inboxId':
        return t('CAMPAIGN.QRBUHO.CREATE.FORM.INBOX.ERROR');
      case 'scheduledAt':
        return t('CAMPAIGN.QRBUHO.CREATE.FORM.SCHEDULED_AT.ERROR');
      case 'selectedAudience':
        return t('CAMPAIGN.QRBUHO.CREATE.FORM.AUDIENCE.ERROR');
      default:
        return t('CAMPAIGN.QRBUHO.CREATE.FORM.TITLE.ERROR');
    }
  }
  return '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title'),
  message: getErrorMessage('message'),
  inbox: getErrorMessage('inboxId'),
  scheduledAt: getErrorMessage('scheduledAt'),
  audience: getErrorMessage('selectedAudience'),
}));

const isSubmitDisabled = computed(() => v$.value.$invalid);

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const validateImageFile = file => {
  const validTypes = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/webp',
  ];
  const maxSize = 5 * 1024 * 1024; // 5MB en bytes

  if (!validTypes.includes(file.type)) {
    return t('CAMPAIGN.QRBUHO.CREATE.FORM.IMAGE.ERROR.INVALID_TYPE');
  }

  if (file.size > maxSize) {
    return t('CAMPAIGN.QRBUHO.CREATE.FORM.IMAGE.ERROR.FILE_TOO_LARGE');
  }

  return null;
};

const handleImageUpload = event => {
  const file = event.target.files[0];
  if (!file) return;

  const error = validateImageFile(file);
  if (error) {
    // TODO: Mostrar notificación de error al usuario
    // Limpiar el input
    event.target.value = '';
    return;
  }

  state.imageFile = file;

  // Crear preview
  const reader = new FileReader();
  reader.onload = e => {
    state.imagePreview = e.target.result;
  };
  reader.readAsDataURL(file);
};

const removeImage = () => {
  state.imageFile = null;
  state.imagePreview = null;
};

const resetState = () => {
  Object.assign(state, initialState);
  v$.value.$reset();
};

const handleCancel = () => emit('cancel');

const handleSubmit = async () => {
  const result = await v$.value.$validate();
  if (!result) return;

  const campaignData = {
    title: state.title,
    message: state.message,
    inbox_id: state.inboxId,
    scheduled_at: formatToUTCString(state.scheduledAt),
    campaign_type: 'one_off',
    audience: state.selectedAudience.map(audienceId => ({
      type: state.labelType === 'conversation' ? 'Label' : 'ContactTag',
      id: audienceId,
    })),
    template_params: {},
  };

  // Usar FormData para enviar archivos correctamente
  const formData = new FormData();
  formData.append('campaign', JSON.stringify(campaignData));

  // Agregar imagen si existe
  if (state.imageFile) {
    formData.append('image', state.imageFile);
  }

  emit('submit', formData);
  resetState();
};

// Watcher para limpiar la audiencia seleccionada cuando cambie el tipo
watch(
  () => state.labelType,
  (newType, oldType) => {
    if (newType !== oldType) {
      // Limpiar la audiencia seleccionada cuando cambie el tipo
      state.selectedAudience = [];
    }
  }
);

// Las etiquetas se cargan automáticamente por el store cuando se necesitan
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <!-- Título -->
    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.QRBUHO.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.QRBUHO.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <!-- Mensaje -->
    <VariableTextArea
      v-model="state.message"
      :label="t('CAMPAIGN.QRBUHO.CREATE.FORM.MESSAGE.LABEL')"
      :placeholder="t('CAMPAIGN.QRBUHO.CREATE.FORM.MESSAGE.PLACEHOLDER')"
      :message="formErrors.message"
      :message-type="formErrors.message ? 'error' : 'info'"
      :max-length="2000"
      show-character-count
    />

    <!-- Imagen -->
    <div class="flex flex-col gap-3">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.QRBUHO.CREATE.FORM.IMAGE.LABEL') }}
      </label>

      <!-- Upload de imagen o preview -->
      <div v-if="!state.imagePreview" class="flex flex-col gap-2">
        <label class="w-full">
          <input
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            class="hidden"
            @change="handleImageUpload"
          />
          <div
            class="w-full justify-center py-3 border-2 border-dashed border-n-alpha-6 hover:border-n-alpha-8 bg-n-alpha-2 hover:bg-n-alpha-3 cursor-pointer rounded-md flex items-center text-sm font-medium text-n-slate-11 hover:text-n-slate-12 transition-colors"
          >
            <svg
              class="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            {{ t('CAMPAIGN.QRBUHO.CREATE.FORM.IMAGE.ADD_BUTTON') }}
          </div>
        </label>
        <p class="text-xs text-n-slate-9 text-center">
          {{ t('CAMPAIGN.QRBUHO.CREATE.FORM.IMAGE.HELP_TEXT') }}
        </p>
      </div>

      <!-- Preview de imagen -->
      <div v-else class="relative">
        <div class="rounded-lg border border-n-alpha-6 p-3 bg-n-alpha-2">
          <div class="flex items-start gap-3">
            <img
              :src="state.imagePreview"
              :alt="state.imageFile?.name"
              class="w-20 h-20 object-cover rounded-md border border-n-alpha-6"
            />
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-n-slate-12 truncate">
                {{ state.imageFile?.name }}
              </p>
              <p class="text-xs text-n-slate-9">
                {{ (state.imageFile?.size / 1024 / 1024).toFixed(2) }}
                {{ t('CAMPAIGN.QRBUHO.CREATE.FORM.IMAGE.SIZE_MB') }}
              </p>
            </div>
            <button
              type="button"
              class="p-1 rounded-md hover:bg-n-alpha-4 text-n-slate-9 hover:text-n-slate-12 transition-colors"
              @click.stop.prevent="removeImage"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Inbox Selection -->
    <div class="flex flex-col gap-1">
      <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.QRBUHO.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        id="inbox"
        v-model="state.inboxId"
        :options="inboxOptions"
        :has-error="!!formErrors.inbox"
        :placeholder="t('CAMPAIGN.QRBUHO.CREATE.FORM.INBOX.PLACEHOLDER')"
        :message="formErrors.inbox"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <!-- Tipo de etiqueta -->
    <div class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.QRBUHO.LABEL_TYPE') }}
      </label>
      <div class="flex gap-0 rounded-lg overflow-hidden p-1 bg-n-alpha-1">
        <button
          type="button"
          class="flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200"
          :class="[
            state.labelType === 'conversation'
              ? 'bg-n-brand text-white shadow-sm'
              : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2',
          ]"
          @click="state.labelType = 'conversation'"
        >
          {{ t('CAMPAIGN.QRBUHO.CONVERSATION') }}
        </button>
        <button
          type="button"
          class="flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200"
          :class="[
            state.labelType === 'contact'
              ? 'bg-n-brand text-white shadow-sm'
              : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2',
          ]"
          @click="state.labelType = 'contact'"
        >
          {{ t('CAMPAIGN.QRBUHO.CONTACT') }}
        </button>
      </div>
    </div>

    <!-- Audiencia -->
    <div class="flex flex-col gap-1">
      <label for="audience" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.QRBUHO.CREATE.FORM.AUDIENCE.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        id="audience"
        v-model="state.selectedAudience"
        :options="audienceList"
        :has-error="!!formErrors.audience"
        :placeholder="t('CAMPAIGN.QRBUHO.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
        :message="formErrors.audience"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <!-- Fecha y hora programada -->
    <Input
      v-model="state.scheduledAt"
      :label="t('CAMPAIGN.QRBUHO.CREATE.FORM.SCHEDULED_AT.LABEL')"
      type="datetime-local"
      :min="currentDateTime"
      :placeholder="t('CAMPAIGN.QRBUHO.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')"
      :message="formErrors.scheduledAt"
      :message-type="formErrors.scheduledAt ? 'error' : 'info'"
    />

    <!-- Botones -->
    <div class="flex gap-3 justify-between items-center w-full">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.QRBUHO.CREATE.CANCEL_BUTTON_TEXT')"
        class="w-full bg-n-alpha-2 text-n-blue-text hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="t('CAMPAIGN.QRBUHO.CREATE.CREATE_BUTTON_TEXT')"
        class="w-full"
        type="submit"
        :is-loading="isCreating"
        :disabled="isCreating || isSubmitDisabled"
      />
    </div>
  </form>
</template>
